import type {
  Seed,
  SeedFacets,
  SeedFacetsResponse,
  SeedListEnvelope,
  SeedList,
  SeedResponse,
  SeedSourceLabel,
  SeedSourceValue,
  SeedStats,
  SeedStatsResponse,
} from '../types';

/**
 * Two vocabularies for one field, on purpose. The API stores `inbox_monster`; the UI shows
 * "Inbox Monster" and keeps that in the URL, which is what the prototype already does. The
 * translation lives here so no page has to know both.
 */
export const SEED_SOURCE_LABEL_BY_VALUE: Record<SeedSourceValue, SeedSourceLabel> = {
  inbox_monster: 'Inbox Monster',
  sparkpost: 'SparkPost',
  other: 'Other',
};

export const SEED_SOURCE_VALUE_BY_LABEL: Record<SeedSourceLabel, SeedSourceValue> = {
  'Inbox Monster': 'inbox_monster',
  SparkPost: 'sparkpost',
  Other: 'other',
};

/** Undefined for anything that is not a known label, so an edited URL sends no source filter. */
export function toSeedSourceValue(label: string): SeedSourceValue | undefined {
  return SEED_SOURCE_VALUE_BY_LABEL[label as SeedSourceLabel];
}

export function seedMapper(row: SeedResponse): Seed {
  return {
    id: row.id,
    email: row.email,
    // Derived by the server. The client stops deriving it, except in the CSV preview, where no
    // row exists yet.
    provider: row.provider,
    source: SEED_SOURCE_LABEL_BY_VALUE[row.source] ?? 'Other',
    groupIds: row.group_ids ?? [],
    // `null` stays `null`: it is the value the "Never seeded" filter matches on.
    lastSeededAt: row.last_seeded_at ?? null,
  };
}

export function seedStatsMapper(stats: SeedStatsResponse): SeedStats {
  return {
    seedCount: stats.seed_count,
    groupCount: stats.group_count,
    neverSeededCount: stats.never_seeded_count,
  };
}

export function seedFacetsMapper(facets: SeedFacetsResponse): SeedFacets {
  return {
    provider: facets.provider,
    source: {
      'Inbox Monster': facets.source.inbox_monster,
      SparkPost: facets.source.sparkpost,
      Other: facets.source.other,
    },
    group: facets.group,
    seeded: facets.seeded,
  };
}

export function seedListMapper(envelope: SeedListEnvelope): SeedList {
  return {
    seeds: envelope.data.map(seedMapper),
    total: envelope.total,
    limit: envelope.limit,
    offset: envelope.offset,
    lastItemIdentifier: envelope.last_item_identifier,
    stats: seedStatsMapper(envelope.stats),
    facets: seedFacetsMapper(envelope.facets),
  };
}
